import {
	Button,
	Col,
	Form,
	InputNumber,
	Row,
	Select,
	Tag,
	Tooltip,
	Alert,
	Empty,
	Divider,
} from "antd";
import React, { useEffect, useState } from "react";
import formaDePagoService from "../../services/formaDePagoService";
import { formatNumberByCountry } from "../../utils/formatNumbers";

const { Option } = Select;

const InvoicePaymentLineForm = ({
	line,
	hasSpecialDiscount,
	onAddLine,
	onUpdateLine,
	onCancel,
	country
}) => {
	const [form] = Form.useForm();
	const [formasDePago, setFormasDePago] = useState([]);
	const [loading, setLoading] = useState(false);
	const [selectedFormaPago, setSelectedFormaPago] = useState(null);
	const [importeRecibido, setImporteRecibido] = useState(0);
	const [cambioCalculado, setCambioCalculado] = useState(0);
	const [originalAmount, setOriginalAmount] = useState(0);

	const isEditing = !!line?.id;
	const pendingAmount = line?.pendingAmount || 0;
	const isAbono = line?.isAbono || false;
	const originalPaymentMethods = line?.originalPaymentMethods || [];
	const hadMultiplePaymentMethods = line?.hadMultiplePaymentMethods || false;
	const hasExistingPayments = line?.hasExistingPayments || false;
	const totalPaymentLines = line?.totalPaymentLines || 1;

	// Verificar si la forma de pago seleccionada es efectivo
	const isEfectivo = selectedFormaPago?.Tipo_Pago === "Efectivo";
	
	// Efecto para cargar las formas de pago al montar el componente
	useEffect(() => {
		loadFormasDePago();
	}, []);

	// Efecto para establecer valores del formulario cuando se está editando
	useEffect(() => {
		if (isEditing && line) {
			const currentAmount = Math.abs(line.Amount) || 0;
			form.setFieldsValue({
				Payment_Method: line.Payment_Method || "",
				Amount: currentAmount,
			});

			// Buscar la forma de pago seleccionada para obtener su descuento
			const formaPago = formasDePago.find(
				(fp) => fp.Code === line.Payment_Method
			);
			if (formaPago) {
				setSelectedFormaPago(formaPago);
				// Para la edición, necesitamos calcular el importe original
				// Si la línea tiene información de descuento, usarla para calcular el original
				const discountPercentage = hasSpecialDiscount ? 0 : line.Dto || 0;
				if (discountPercentage > 0) {
					// Si hay descuento registrado en la línea, calcular el importe original
					const originalAmountValue = currentAmount / (1 - discountPercentage / 100);
					setOriginalAmount(originalAmountValue);
				} else {
					// Si no hay descuento registrado, el importe actual es el original
					setOriginalAmount(currentAmount);
				}
			} else {
				setOriginalAmount(currentAmount);
			}
		} else if (!isEditing) {
			form.resetFields();
			// Establecer el importe pendiente como valor por defecto
			form.setFieldsValue({
				Amount: pendingAmount,
			});
			// Para nuevos pagos, el importe original es el pendingAmount
			setOriginalAmount(pendingAmount);
		}
	}, [form, line, isEditing, pendingAmount, formasDePago]);

	// Efecto para calcular el cambio cuando cambia el importe recibido o el importe a pagar
	useEffect(() => {
		if (isEfectivo && importeRecibido > 0) {
			const importeAPagar = form.getFieldValue("Amount") || 0;
			const cambio = importeRecibido - importeAPagar;
			setCambioCalculado(cambio > 0 ? cambio : 0);
		} else {
			setCambioCalculado(0);
		}
	}, [importeRecibido, isEfectivo, form]);

	const loadFormasDePago = async () => {
		try {
			setLoading(true);
			const formaPagoList = await formaDePagoService.getAllFormasDePago();
			setFormasDePago(formaPagoList);
		} catch (error) {
			console.error("Error al cargar formas de pago:", error);
			window.messageApi.error("No se pudieron cargar las formas de pago");
		} finally {
			setLoading(false);
		}
	};

	// Calcular el importe con descuento aplicado
	const calculateAdjustedAmount = (amount, discount) => {
		if (!amount) return 0;

		// Si hay descuento, calcularlo directamente
		if (discount > 0) {
			const discountAmount = amount * (discount / 100);
			return amount - discountAmount;
		}

		return amount;
	};

	const handleFormaPagoChange = (value) => {
		const formaPago = formasDePago.find((fp) => fp.Code === value);
		setSelectedFormaPago(formaPago);

		// Resetear valores de efectivo cuando se cambia la forma de pago
		setImporteRecibido(0);
		setCambioCalculado(0);

		// Si la factura original tenía múltiples formas de pago y es un abono,
		// no aplicamos descuento aunque la forma de pago tenga uno asignado
		if (isAbono && hadMultiplePaymentMethods) {
			// Restaurar el importe original sin descuento
			form.setFieldsValue({ Amount: originalAmount });
			return;
		}

		// Si ya hay líneas de pago existentes o habrá múltiples líneas de pago,
		// no aplicar descuento
		if (hasExistingPayments || totalPaymentLines > 1) {
			// Restaurar el importe original sin descuento
			form.setFieldsValue({ Amount: originalAmount });
			return;
		}

		// Al cambiar la forma de pago, calcular el descuento SIEMPRE sobre el importe original
		const baseAmount = originalAmount || pendingAmount;
		const adjustedAmount = calculateAdjustedAmount(
			baseAmount,
			hasSpecialDiscount ? 0 : formaPago?.Dto || 0
		);

		// Actualizar el importe en el formulario
		form.setFieldsValue({ Amount: adjustedAmount });
	};

	const handleImporteRecibidoChange = (value) => {
		setImporteRecibido(value || 0);
	};

	const handleAmountChange = (value) => {
		// Cuando cambia el importe a pagar, recalcular el cambio
		if (isEfectivo && importeRecibido > 0) {
			const cambio = importeRecibido - (value || 0);
			setCambioCalculado(cambio > 0 ? cambio : 0);
		}
	};

	const handleSubmit = async (values) => {
		
		try {
			// Validar que para efectivo, el importe recibido sea suficiente
			if (
				isEfectivo &&
				importeRecibido > 0 &&
				importeRecibido < values.Amount
			) {
				window.messageApi.error(
					"El importe recibido no puede ser menor al importe a pagar"
				);
				return;
			}

			let lineData = {
				...values,
				// Añadir campos específicos para efectivo
				...(isEfectivo && {
					importeRecibido: importeRecibido,
					cambio: cambioCalculado,
				}),
			};

			// Si es un abono y la factura original tenía múltiples formas de pago,
			// no incluir descuento aunque la forma de pago lo tenga
			if (isAbono && hadMultiplePaymentMethods) {
				lineData.Dto = 0;
				lineData.Dto_Amount = 0;
			}
			// Si ya hay líneas de pago existentes o habrá múltiples líneas de pago,
			// no aplicar descuento
			if (hasExistingPayments || totalPaymentLines > 1) {
				lineData.Dto = 0;
				lineData.Dto_Amount = 0;
			}
			if (!hasExistingPayments && totalPaymentLines == 1) {
				const discountPercentage = hasSpecialDiscount ? 0 : selectedFormaPago?.Dto || 0;
				lineData.Dto = discountPercentage;

				// Calcular el monto del descuento basado en el porcentaje
				// Usar el importe original que ya tenemos calculado
				if (discountPercentage > 0) {
					// El Amount ya viene ajustado por el descuento en el formulario
					// Usar el originalAmount que ya tenemos para calcular el descuento
					const baseAmount = originalAmount || pendingAmount;
					const discountAmount = baseAmount * (discountPercentage / 100);
					lineData.Dto_Amount = parseFloat(discountAmount.toFixed(2));
				} else {
					lineData.Dto_Amount = 0;
				}
			}

			if (isEditing) {
				await onUpdateLine({ ...line, ...lineData });
			} else {
				await onAddLine(lineData);
			}

			// Reiniciar el formulario después de enviar
			form.resetFields();
			setImporteRecibido(0);
			setCambioCalculado(0);
		} catch (error) {
			console.error("Error al guardar línea de pago:", error);
			window.messageApi.error("No se pudo guardar la línea de pago");
		}
	};

	// Filtrar las formas de pago disponibles si estamos en un abono
	const getAvailablePaymentMethods = () => {
		if (isAbono && originalPaymentMethods.length > 0) {
			// Si es un abono, filtrar para mostrar solo las formas de pago originales
			return formasDePago.filter((fp) =>
				originalPaymentMethods.includes(fp.Code)
			);
		}
		return formasDePago;
	};

	const availablePaymentMethods = getAvailablePaymentMethods();

	return (
		<Form
			form={form}
			layout="vertical"
			onFinish={handleSubmit}
			initialValues={{
				Amount: pendingAmount,
			}}
		>
			{isAbono && (
				<Alert
					message="Línea de pago para nota de crédito"
					description={
						hadMultiplePaymentMethods
							? "Está creando un pago para una nota de crédito de una factura que tenía múltiples formas de pago. No se aplicará ningún descuento aunque seleccione una forma de pago con descuento, para mantener la coherencia con la factura original."
							: "Está creando un pago para una nota de crédito. El importe debe ser positivo y coincidir con el valor de los productos que está devolviendo. Solo puede usar las mismas formas de pago que se utilizaron en la factura original. Recuerde que si utiliza más de un método de pago, no se aplicará ningún descuento."
					}
					type="info"
					showIcon
					style={{ marginBottom: 16 }}
				/>
			)}

			{!isAbono && (
				<Alert
					message="Información sobre descuentos"
					description={
						hasExistingPayments || totalPaymentLines > 1
							? "Como ya hay múltiples formas de pago o se añadirá una segunda forma de pago, no se aplicará ningún descuento de forma de pago, incluso si alguno de los métodos tiene descuento asociado."
							: "Si utiliza más de un método de pago, no se aplicará ningún descuento de forma de pago, incluso si alguno de los métodos tiene descuento asociado."
					}
					type={
						hasExistingPayments || totalPaymentLines > 1 ? "warning" : "info"
					}
					showIcon
					style={{ marginBottom: 16 }}
				/>
			)}

			<Row gutter={16}>
				<Col xs={24} sm={12}>
					<Form.Item
						name="Payment_Method"
						label="Forma de pago"
						rules={[
							{ required: true, message: "Seleccione una forma de pago" },
						]}
						className="invoice-payment-line-form-item"
					>
						<Select
							showSearch
							placeholder="Seleccione una forma de pago"
							optionFilterProp="children"
							loading={loading}
							onChange={handleFormaPagoChange}
							filterOption={(input, option) =>
								(option?.label || "")
									.toLowerCase()
									.includes(input.toLowerCase())
							}
							notFoundContent={
								isAbono && originalPaymentMethods.length === 0 ? (
									<Empty
										description="No se encontraron formas de pago en la factura original"
										image={Empty.PRESENTED_IMAGE_SIMPLE}
									/>
								) : null
							}
						>
							{availablePaymentMethods.map((fp) => (
								<Option
									key={fp.id}
									value={fp.Code}
									label={`${fp.Code} - ${fp.Description}`}
								>
									{fp.Code} - {fp.Description}
									{!hasSpecialDiscount && fp.Dto > 0 && (
										<Tag
											color={
												(isAbono && hadMultiplePaymentMethods) ||
													hasExistingPayments ||
													totalPaymentLines > 1
													? "orange"
													: "green"
											}
											style={{ marginLeft: 8 }}
										>
											Dto: {fp.Dto}%
											{((isAbono && hadMultiplePaymentMethods) ||
												hasExistingPayments ||
												totalPaymentLines > 1) && (
													<span style={{ color: "#ff4d4f" }}>
														{" "}
														(No aplicable)
													</span>
												)}
										</Tag>
									)}
									{fp.Tipo_Pago === "Efectivo" && (
										<Tag className="efectivo-tag" style={{ marginLeft: 8 }}>
											Efectivo
										</Tag>
									)}
								</Option>
							))}
						</Select>
					</Form.Item>
					{selectedFormaPago?.Dto > 0 && (
						<div style={{ marginTop: -15, marginBottom: 15 }}>
							<Tooltip
								title={
									isAbono && hadMultiplePaymentMethods
										? "Este descuento no se aplicará porque la factura original tenía múltiples formas de pago"
										: hasSpecialDiscount ?
											"Este descuento no se aplicara porque los descuentos no son acumulables"
											: hasExistingPayments || totalPaymentLines > 1
												? "Este descuento no se aplicará porque hay múltiples formas de pago"
												: "Este descuento se aplica directamente al importe"
								}
							>
								<Tag
									color={
										(isAbono && hadMultiplePaymentMethods) ||
											hasExistingPayments ||
											totalPaymentLines > 1 ||
											hasSpecialDiscount
											? "orange"
											: "green"
									}
								>
									{
										selectedFormaPago && isEfectivo ? `Esta forma de pago incluye un descuento del ${selectedFormaPago.Dto}%` : ''
									}
									{((isAbono && hadMultiplePaymentMethods) ||
										hasExistingPayments ||
										totalPaymentLines > 1 ||
										hasSpecialDiscount) && (
											<span style={{ color: "#ff4d4f" }}>
												{
													hasSpecialDiscount ? " (No aplicable, descuento existente" :
														(isAbono && hadMultiplePaymentMethods
															? " (No aplicable en este abono)"
															: " (No aplicable con múltiples pagos)")
												}
											</span>
										)}
								</Tag>
							</Tooltip>
						</div>
					)}
				</Col>

				<Col xs={24} sm={12}>
					<Form.Item
						name="Amount"
						label="Importe a pagar"
						rules={[
							{ required: true, message: "Ingrese un importe" },
							{
								validator: (_, value) => {
									if (value > 0) {
										return Promise.resolve();
									}
									return Promise.reject(
										new Error("El importe debe ser mayor que 0")
									);
								},
							},
						]}
						className="invoice-payment-line-form-item"
						tooltip={
							isAbono ? "Introduzca el importe a devolver (valor positivo)" : ""
						}
					>
						<InputNumber
							min={0}
							style={{ width: "100%" }}
							precision={2}
							placeholder="Importe a pagar"
							addonAfter={`${country?.currencySymbol}`}
							onChange={handleAmountChange}
						/>
					</Form.Item>
				</Col>
			</Row>

			{/* Campos específicos para efectivo */}
			{isEfectivo && (
				<>
					<Divider orientation="left">Cálculo de cambio</Divider>
					<Alert
						message="Pago en efectivo"
						description="Introduzca el importe que recibió del cliente para calcular automáticamente el cambio a devolver."
						type="info"
						showIcon
						style={{ marginBottom: 16 }}
					/>

					<Row gutter={16}>
						<Col xs={24} sm={8}>
							<Form.Item
								label="Importe recibido del cliente"
								tooltip="Cantidad de dinero que le entregó el cliente"
							>
								<InputNumber
									min={0}
									style={{ width: "100%" }}
									precision={2}
									placeholder="Importe recibido"
									addonAfter={`${country?.currencySymbol}`}
									value={importeRecibido}
									onChange={handleImporteRecibidoChange}
								/>
							</Form.Item>
						</Col>

						<Col xs={24} sm={8}>
							<Form.Item
								label="Cambio a devolver"
								tooltip="Diferencia entre el importe recibido y el importe a pagar"
							>
								<InputNumber
									style={{ width: "100%" }}
									precision={2}
									value={cambioCalculado}
									disabled
									addonAfter={`${country?.currencySymbol}`}
									className={cambioCalculado > 0 ? "cambio-positivo" : ""}
								/>
							</Form.Item>
						</Col>

						<Col xs={24} sm={8}>
							<div
								className={`cambio-display ${cambioCalculado > 0 ? "positivo" : "neutro"
									}`}
							>
								<div className="cambio-display-label">
									{cambioCalculado > 0 ? "Devolver al cliente:" : "Cambio:"}
								</div>
								<div className="cambio-display-amount">
									{formatNumberByCountry(cambioCalculado, country, true)}
								</div>
							</div>
						</Col>
					</Row>

					{importeRecibido > 0 &&
						importeRecibido < (form.getFieldValue("Amount") || 0) && (
							<Alert
								message="Importe insuficiente"
								description={`El cliente debe entregar al menos ${formatNumberByCountry((form.getFieldValue("Amount") || 0), country, true)
									}. Faltan ${formatNumberByCountry((form.getFieldValue("Amount") || 0) - importeRecibido, country, true)
									} .`}
								type="error"
								showIcon
								style={{ marginBottom: 16 }}
							/>
						)}
				</>
			)}

			<div className="invoice-form-actions">
				<Button onClick={onCancel}>Cancelar</Button>
				<Button type="primary" htmlType="submit">
					{isEditing ? "Actualizar línea de pago" : "Añadir línea de pago"}
				</Button>
			</div>
		</Form>
	);
};

export default InvoicePaymentLineForm;
